import React, { useState, useRef, useEffect } from 'react';
import { Copy, Check, FileJson, FileCode, AlertCircle, CheckCircle, RefreshCw } from 'lucide-react';
import { cn } from '../../lib/utils';

type EditorMode = 'json' | 'yaml';

interface ValidationError {
  line: number;
  column: number;
  message: string;
  severity: 'error' | 'warning';
}

interface CodeEditorProps {
  value: string;
  mode?: EditorMode;
  onChange?: (value: string) => void;
  onValidate?: (errors: ValidationError[]) => void;
  readOnly?: boolean;
  placeholder?: string;
  minHeight?: string;
  maxHeight?: string;
  showLineNumbers?: boolean;
  showToolbar?: boolean;
  className?: string;
}

// Simple JSON/YAML validator
const validateContent = (content: string, mode: EditorMode): ValidationError[] => {
  const errors: ValidationError[] = [];
  
  if (!content.trim()) return errors;

  if (mode === 'json') {
    try {
      JSON.parse(content);
    } catch (e: any) {
      const match = e.message.match(/position (\d+)/);
      const position = match ? parseInt(match[1]) : 0;
      const lines = content.substring(0, position).split('\n');
      const line = lines.length;
      const column = lines[lines.length - 1]?.length || 0;
      
      errors.push({
        line,
        column,
        message: e.message,
        severity: 'error',
      });
    }
  } else if (mode === 'yaml') {
    // Basic YAML validation (check for common issues)
    const lines = content.split('\n');
    lines.forEach((lineContent, index) => {
      // Check for tabs (YAML prefers spaces)
      if (lineContent.includes('\t')) {
        errors.push({
          line: index + 1,
          column: lineContent.indexOf('\t') + 1,
          message: 'YAML prefers spaces over tabs for indentation',
          severity: 'warning',
        });
      }
      // Check for inconsistent indentation
      const leadingSpaces = lineContent.match(/^(\s*)/)?.[1].length || 0;
      if (leadingSpaces % 2 !== 0 && leadingSpaces > 0) {
        errors.push({
          line: index + 1,
          column: 1,
          message: 'Inconsistent indentation (should be multiple of 2)',
          severity: 'warning',
        });
      }
    });
  }

  return errors;
};

// Convert JSON to YAML (simple implementation)
const jsonToYaml = (json: string): string => {
  try {
    const obj = JSON.parse(json);
    return objectToYaml(obj, 0);
  } catch {
    return json;
  }
};

const objectToYaml = (obj: any, indent: number): string => {
  const spaces = '  '.repeat(indent);
  let result = '';

  if (Array.isArray(obj)) {
    obj.forEach((item) => {
      if (typeof item === 'object' && item !== null) {
        result += `${spaces}-\n${objectToYaml(item, indent + 1)}`;
      } else {
        result += `${spaces}- ${formatYamlValue(item)}\n`;
      }
    });
  } else if (typeof obj === 'object' && obj !== null) {
    Object.entries(obj).forEach(([key, value]) => {
      if (typeof value === 'object' && value !== null) {
        result += `${spaces}${key}:\n${objectToYaml(value, indent + 1)}`;
      } else {
        result += `${spaces}${key}: ${formatYamlValue(value)}\n`;
      }
    });
  }

  return result;
};

const formatYamlValue = (value: any): string => {
  if (typeof value === 'string') {
    if (value.includes('\n') || value.includes(':') || value.includes('#')) {
      return `"${value.replace(/"/g, '\\"')}"`;
    }
    return value;
  }
  if (value === null) return 'null';
  if (value === undefined) return '';
  return String(value);
};

// Convert YAML to JSON (simple implementation)
const yamlToJson = (yaml: string): string => {
  try {
    const obj = parseSimpleYaml(yaml);
    return JSON.stringify(obj, null, 2);
  } catch {
    return yaml;
  }
};

const parseSimpleYaml = (yaml: string): any => {
  const lines = yaml.split('\n').filter(line => line.trim() && !line.trim().startsWith('#'));
  const result: any = {};
  let currentObj = result;
  const stack: { obj: any; indent: number }[] = [{ obj: result, indent: -1 }];

  lines.forEach((line) => {
    const indent = line.search(/\S/);
    const content = line.trim();

    // Pop stack until we find parent
    while (stack.length > 1 && stack[stack.length - 1].indent >= indent) {
      stack.pop();
    }
    currentObj = stack[stack.length - 1].obj;

    if (content.startsWith('- ')) {
      // Array item
      const value = content.substring(2).trim();
      if (!Array.isArray(currentObj)) {
        const lastKey = Object.keys(currentObj).pop();
        if (lastKey) {
          currentObj[lastKey] = [];
          currentObj = currentObj[lastKey];
        }
      }
      if (Array.isArray(currentObj)) {
        currentObj.push(parseYamlValue(value));
      }
    } else if (content.includes(':')) {
      const colonIndex = content.indexOf(':');
      const key = content.substring(0, colonIndex).trim();
      const value = content.substring(colonIndex + 1).trim();

      if (value) {
        currentObj[key] = parseYamlValue(value);
      } else {
        currentObj[key] = {};
        stack.push({ obj: currentObj[key], indent });
      }
    }
  });

  return result;
};

const parseYamlValue = (value: string): any => {
  if (value === 'true') return true;
  if (value === 'false') return false;
  if (value === 'null') return null;
  if (/^-?\d+$/.test(value)) return parseInt(value);
  if (/^-?\d+\.\d+$/.test(value)) return parseFloat(value);
  if ((value.startsWith('"') && value.endsWith('"')) || (value.startsWith("'") && value.endsWith("'"))) {
    return value.slice(1, -1);
  }
  return value;
};

// Format JSON
const formatJson = (json: string): string => {
  try {
    return JSON.stringify(JSON.parse(json), null, 2);
  } catch {
    return json;
  }
};

export const CodeEditor: React.FC<CodeEditorProps> = ({
  value,
  mode = 'json',
  onChange,
  onValidate,
  readOnly = false,
  placeholder = 'Enter code here...',
  minHeight = '200px',
  maxHeight = '500px',
  showLineNumbers = true,
  showToolbar = true,
  className,
}) => {
  const [currentMode, setCurrentMode] = useState<EditorMode>(mode);
  const [errors, setErrors] = useState<ValidationError[]>([]);
  const [copied, setCopied] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  // Validate on content change
  useEffect(() => {
    const validationErrors = validateContent(value, currentMode);
    setErrors(validationErrors);
    onValidate?.(validationErrors);
  }, [value, currentMode, onValidate]);

  // Handle mode switch
  const handleModeSwitch = (newMode: EditorMode) => {
    if (newMode === currentMode) return;

    let convertedValue = value;
    if (newMode === 'yaml' && currentMode === 'json') {
      convertedValue = jsonToYaml(value);
    } else if (newMode === 'json' && currentMode === 'yaml') {
      convertedValue = yamlToJson(value);
    }

    setCurrentMode(newMode);
    onChange?.(convertedValue);
  };

  // Handle format
  const handleFormat = () => {
    if (currentMode === 'json') {
      onChange?.(formatJson(value));
    }
  };

  // Handle copy
  const handleCopy = async () => {
    await navigator.clipboard.writeText(value);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  // Get line numbers
  const lineCount = value.split('\n').length;
  const lineNumbers = Array.from({ length: Math.max(lineCount, 10) }, (_, i) => i + 1);

  // Get error lines for highlighting
  const errorLines = new Set(errors.filter(e => e.severity === 'error').map(e => e.line));
  const warningLines = new Set(errors.filter(e => e.severity === 'warning').map(e => e.line));

  return (
    <div className={cn('rounded-lg border border-surface-600 overflow-hidden', className)}>
      {/* Toolbar */}
      {showToolbar && (
        <div className="flex items-center justify-between px-3 py-2 bg-surface-800 border-b border-surface-600">
          <div className="flex items-center gap-2">
            {/* Mode Switcher */}
            <div className="flex items-center bg-surface-700 rounded-lg p-0.5">
              <button
                type="button"
                onClick={() => handleModeSwitch('json')}
                className={cn(
                  'flex items-center gap-1.5 px-3 py-1 text-xs font-medium rounded-md transition-colors',
                  currentMode === 'json'
                    ? 'bg-primary-500 text-white'
                    : 'text-surface-400 hover:text-white'
                )}
              >
                <FileJson className="w-3.5 h-3.5" />
                JSON
              </button>
              <button
                type="button"
                onClick={() => handleModeSwitch('yaml')}
                className={cn(
                  'flex items-center gap-1.5 px-3 py-1 text-xs font-medium rounded-md transition-colors',
                  currentMode === 'yaml'
                    ? 'bg-primary-500 text-white'
                    : 'text-surface-400 hover:text-white'
                )}
              >
                <FileCode className="w-3.5 h-3.5" />
                YAML
              </button>
            </div>

            {/* Validation Status */}
            {errors.length > 0 ? (
              <div className="flex items-center gap-1.5 text-xs">
                <AlertCircle className="w-3.5 h-3.5 text-red-400" />
                <span className="text-red-400">{errors.length} issue(s)</span>
              </div>
            ) : value.trim() ? (
              <div className="flex items-center gap-1.5 text-xs">
                <CheckCircle className="w-3.5 h-3.5 text-green-400" />
                <span className="text-green-400">Valid</span>
              </div>
            ) : null}
          </div>

          <div className="flex items-center gap-1">
            {/* Format Button */}
            {currentMode === 'json' && !readOnly && (
              <button
                type="button"
                onClick={handleFormat}
                className="p-1.5 text-surface-400 hover:text-white hover:bg-surface-700 rounded transition-colors"
                title="Format"
              >
                <RefreshCw className="w-4 h-4" />
              </button>
            )}

            {/* Copy Button */}
            <button
              type="button"
              onClick={handleCopy}
              className="p-1.5 text-surface-400 hover:text-white hover:bg-surface-700 rounded transition-colors"
              title="Copy"
            >
              {copied ? (
                <Check className="w-4 h-4 text-green-400" />
              ) : (
                <Copy className="w-4 h-4" />
              )}
            </button>
          </div>
        </div>
      )}

      {/* Editor Area */}
      <div
        className="flex bg-surface-900 overflow-auto"
        style={{ minHeight, maxHeight }}
      >
        {/* Line Numbers */}
        {showLineNumbers && (
          <div className="flex-shrink-0 py-3 px-2 bg-surface-800 border-r border-surface-700 select-none">
            {lineNumbers.map((num) => (
              <div
                key={num}
                className={cn(
                  'text-xs text-right pr-2 leading-6 font-mono',
                  errorLines.has(num)
                    ? 'text-red-400 bg-red-500/10'
                    : warningLines.has(num)
                    ? 'text-yellow-400 bg-yellow-500/10'
                    : 'text-surface-500'
                )}
                style={{ minWidth: '2rem' }}
              >
                {num}
              </div>
            ))}
          </div>
        )}

        {/* Code Area */}
        <textarea
          ref={textareaRef}
          value={value}
          onChange={(e) => onChange?.(e.target.value)}
          readOnly={readOnly}
          placeholder={placeholder}
          spellCheck={false}
          className={cn(
            'flex-1 p-3 bg-transparent text-white font-mono text-sm leading-6 resize-none',
            'focus:outline-none placeholder-surface-600',
            readOnly && 'cursor-default'
          )}
          style={{ minHeight }}
        />
      </div>

      {/* Error Panel */}
      {errors.length > 0 && (
        <div className="px-3 py-2 bg-surface-800 border-t border-surface-600 max-h-24 overflow-auto">
          {errors.map((error, index) => (
            <div
              key={index}
              className={cn(
                'flex items-start gap-2 text-xs py-1',
                error.severity === 'error' ? 'text-red-400' : 'text-yellow-400'
              )}
            >
              <AlertCircle className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
              <span>
                Line {error.line}, Col {error.column}: {error.message}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default CodeEditor;
